import { z } from 'zod';
import { BRAZIL_UFS, isValidDocument, normalizeDocument, onlyDigits } from '../documents';
import { isValidPhoneBR } from '../phone';
import { booleanQuery, optionalText, paginationQuerySchema, requiredText } from './common';

const phoneField = z
  .string({ required_error: 'Telefone é obrigatório' })
  .trim()
  .min(1, 'Telefone é obrigatório')
  .refine(isValidPhoneBR, 'Telefone inválido — informe DDD + número')
  .transform(onlyDigits);

const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .max(160)
  .nullish()
  .refine((v) => !v || z.string().email().safeParse(v).success, 'E-mail inválido')
  .transform((v) => (v === undefined ? undefined : v ? v : null));

const documentField = z
  .string()
  .trim()
  .max(20)
  .nullish()
  .refine((v) => !v || isValidDocument(v), 'CPF/CNPJ inválido')
  .transform((v) => (v === undefined ? undefined : v ? normalizeDocument(v) : null));

const stateField = z
  .string()
  .trim()
  .toUpperCase()
  .nullish()
  .refine((v) => !v || (BRAZIL_UFS as readonly string[]).includes(v), 'UF inválida')
  .transform((v) => (v === undefined ? undefined : v ? v : null));

const zipField = z
  .string()
  .trim()
  .nullish()
  .refine((v) => !v || onlyDigits(v).length === 8, 'CEP deve ter 8 dígitos')
  .transform((v) => (v === undefined ? undefined : v ? onlyDigits(v) : null));

export const clientBaseSchema = z.object({
  name: requiredText(120, 'Nome', 2),
  phone: phoneField,
  email: emailField,
  document: documentField,
  street: optionalText(160, 'Rua'),
  number: optionalText(20, 'Número'),
  complement: optionalText(80, 'Complemento'),
  reference: optionalText(200, 'Condomínio / ponto de referência'),
  neighborhood: optionalText(80, 'Bairro'),
  city: optionalText(80, 'Cidade'),
  state: stateField,
  zip: zipField,
  notes: optionalText(2000, 'Observações'),
});

export const clientCreateSchema = clientBaseSchema;
export const clientUpdateSchema = clientBaseSchema.partial();
export type ClientCreateInput = z.infer<typeof clientCreateSchema>;
export type ClientUpdateInput = z.infer<typeof clientUpdateSchema>;
export type ClientFormValues = z.input<typeof clientCreateSchema>;

export const clientListQuerySchema = paginationQuerySchema.extend({
  includeInactive: booleanQuery,
});
